/**
 * Все строки интерфейса. Лежит в shared, а не у клиента: их читают И web,
 * И нативное приложение, И выгрузка на вычитку носителем
 * (`scripts/extract-az.ts`). Копия у каждого клиента разошлась бы при первой же
 * правке, а выгрузка показывала бы носителю только половину приложения.
 *
 * Ключи совпадают у обеих платформ — расхождение означало бы, что одно и то же
 * место названо в двух приложениях по-разному.
 */
import type { Lang } from './cards.ts'

/** Все строки интерфейса. Ключи совпадают с `t.*` из макета Sirr Tarot.dc.html. */
/**
 * Словарь. Наружу отдан сырым, без отката на английский: тест полноты обязан
 * видеть пустой перевод, а `makeT` его как раз и прячет — в этом его работа.
 */
export const DICT = {
  tagline:        ['древняя книга тайн', 'qədim sirlər kitabı',
                   'the ancient book of secrets', 'kadim sırlar kitabı'],
  welcomeText:    ['Карты хранят ответы, что вы уже знаете. Позвольте им заговорить.',
                   'Kartlar sizin artıq bildiyiniz cavabları saxlayır. Qoy danışsınlar.',
                   'The cards hold answers you already know. Let them speak.', 'Kartlar zaten bildiğiniz cevapları saklar. Bırakın konuşsunlar.'],
  // Подпись кнопки «назад» — для голосового доступа. В вебе она была зашита
  // по-русски прямо в `aria-label`, то есть незрячий человек с азербайджанским
  // слышал русское слово.
  back:           ['Назад', 'Geri',
                   'Back', 'Geri'],
  step:           ['Шаг', 'Addım',
                   'Step', 'Adım'],
  goalsTitle:     ['Что вас ведёт?', 'Sizi nə aparır?',
                   'What guides you?', 'Sizi ne yönlendiriyor?'],
  goalsSub:       ['Выберите одну или несколько сфер', 'Bir və ya bir neçə sahə seçin',
                   'Choose one area or several', 'Bir ya da birkaç alan seçin'],
  pathTitle:      ['Ваш путь', 'Yolunuz',
                   'Your path', 'Yolunuz'],
  pathSub:        ['Насколько вы близки к картам?', 'Kartlara nə qədər yaxınsınız?',
                   'How close are you to the cards?', 'Kartlara ne kadar yakınsınız?'],
  // ── Онбординг-разговор ───────────────────────────────────────────
  // Знакомство идёт репликами, а не анкетой: спрашивает персонаж, отвечает
  // человек, и каждый ответ можно поправить, вернувшись к нему.
  //
  // Под каждым вопросом идёт строка «зачем спрашиваем». Вопрос без причины
  // читается как сбор данных; с причиной — как забота о точности толкования.
  // Поэтому у части вопросов есть отдельная «спрашивающая» формулировка:
  // «Ваш путь» — это заголовок, а не то, что говорят вслух.
  obHello:        ['Прежде чем открыть карты, я спрошу немного о вас. Поправить ответ можно в любой момент — просто коснитесь его.',
                   'Kartları açmazdan əvvəl sizin haqqınızda bir az soruşacağam. Cavabı istənilən vaxt düzəltmək olar — sadəcə ona toxunun.',
                   'Before I open the cards, I will ask a little about you. You can change any answer at any time — just tap it.',
                   'Kartları açmadan önce size dair birkaç şey soracağım. Cevabı istediğiniz an değiştirebilirsiniz — dokunmanız yeterli.'],
  whyGoals:       ['Это фон для акцентов. В самом толковании я о них не заговорю первым.',
                   'Bu, vurğular üçün fondur. Şərhin özündə onlardan birinci mən danışmayacağam.',
                   'This is background for emphasis. I will not bring these up first in the reading itself.',
                   'Bu, vurgular için arka plandır. Yorumun kendisinde bunlardan ilk ben söz etmeyeceğim.'],
  askPath:        ['Насколько вы близки к картам?', 'Kartlara nə qədər yaxınsınız?',
                   'How close are you to the cards?', 'Kartlara ne kadar yakınsınız?'],
  whyPath:        ['От этого зависит, насколько подробно я объясняю.',
                   'Bundan asılıdır ki, nə qədər ətraflı izah edim.',
                   'This decides how much I explain along the way.',
                   'Bu, ne kadar ayrıntılı anlatacağımı belirler.'],
  askSign:        ['Какой у вас знак?', 'Bürcünüz hansıdır?',
                   'What is your sign?', 'Burcunuz hangisi?'],
  // Отклик на ответ. Без него разговор — всё та же анкета, только пузырями:
  // человек говорит, и ему никто не отвечает. Формы намеренно без рода —
  // кто именно ведёт знакомство, мы не называем, а «запомнила» / «запомнил»
  // пришлось бы выбирать.
  ackName:        ['Приятно познакомиться, %s.', 'Tanış olduğuma şadam, %s.',
                   'Good to meet you, %s.', 'Tanıştığımıza sevindim, %s.'],
  ackGender:      ['Спасибо. Так и буду обращаться.',
                   'Sağ olun. Elə bu cür də müraciət edəcəyəm.',
                   'Thank you. That is how I will speak to you.',
                   'Teşekkürler. Size böyle hitap edeceğim.'],
  ackGoals:       ['Понятно. Буду держать это в виду.',
                   'Aydındır. Bunu nəzərdə saxlayacağam.',
                   'I see. I will keep that in mind.',
                   'Anladım. Bunu aklımda tutacağım.'],
  ackPath:        ['Хорошо, найду верную меру.', 'Yaxşı, düzgün ölçünü tapacağam.',
                   'Good, I will find the right measure.', 'Peki, doğru ölçüyü bulacağım.'],
  obDone:         ['Этого хватит. Остальное скажут карты.',
                   'Bu, bəsdir. Qalanını kartlar deyəcək.',
                   'That is enough. The cards will say the rest.',
                   'Bu kadarı yeter. Gerisini kartlar söyleyecek.'],
  // Пол спрашиваем прямо и сразу объясняем зачем: вопрос без причины читается
  // как сбор данных, а с причиной — как забота о точности толкования.
  genderTitle:    ['Кто вы?', 'Siz kimsiniz?',
                   'Who are you?', 'Kimsiniz?'],
  genderSub:      ['Чтобы карты говорили с вами, а не с кем-то вообще',
                   'Kartlar hər kəslə yox, məhz sizinlə danışsın deyə',
                   'So the cards speak to you, not to someone in general',
                   'Kartlar herkesle değil, sizinle konuşsun diye'],
  signTitle:      ['Ваш знак', 'Bürcünüz',
                   'Your sign', 'Burcunuz'],
  signSub:        ['Для точных гаданий и совместимости', 'Dəqiq fallar və uyğunluq üçün',
                   'For precise readings and compatibility', 'Daha isabetli açılımlar ve uyum için'],

  // ── Имя и дата рождения ──────────────────────────────────────────
  // Имя спрашиваем сами, а не берём молча из Google или Telegram: там оно
  // сплошь и рядом неполное или вовсе почта, а уходит оно в обращение модели.
  // Язык спрашиваем сами, а не берём из настроек телефона. В Азербайджане
  // азербайджанского в настройках у многих просто нет — телефон стоит на
  // русском или турецком, а приложение на родном языке им как раз и нужно.
  // `langTitle` уже занят подписью раздела в профиле — здесь свой ключ.
  askLang:        ['На каком языке говорить?', 'Hansı dildə danışaq?',
                   'Which language shall we speak?', 'Hangi dilde konuşalım?'],
  whyLang:        ['Толкования и разговоры будут на нём. Поменять можно в профиле.',
                   'Şərhlər və söhbətlər bu dildə olacaq. Profildə dəyişmək olar.',
                   'Readings and conversations will be in it. You can change it in your profile.',
                   'Yorumlar ve sohbetler bu dilde olacak. Profilden değiştirebilirsiniz.'],
  ackLang:        ['Хорошо, говорим по-русски.', 'Yaxşı, Azərbaycan dilində danışırıq.',
                   'Good, we will speak English.', 'Tamam, Türkçe konuşuyoruz.'],
  nameTitle:      ['Как вас зовут?', 'Adınız nədir?',
                   'What is your name?', 'Adınız nedir?'],
  nameSub:        ['Карты будут обращаться к вам по имени.',
                   'Kartlar sizə adınızla müraciət edəcək.',
                   'The cards will address you by name.',
                   'Kartlar size adınızla hitap edecek.'],
  nameHint:       ['Ваше имя', 'Adınız',
                   'Your name', 'Adınız'],
  // Правило показываем заранее — тем же, которым его проверяет сервер
  // (`shared/name.ts`), иначе обращения не будет и человек не поймёт почему.
  nameRule:       ['Одно слово, только буквы — от 2 до 20.',
                   'Bir kəlmə, yalnız hərflər — 2-dən 20-yə.',
                   'One word, letters only — 2 to 20.',
                   'Tek kelime, sadece harfler — 2 ile 20 arası.'],
  // ── Отказ в доступе к камере или галерее ─────────────────────────
  // Случай, которого нет в браузере: там выбор файла разрешений не требует.
  // Смешивать его с «снимок не читается» нельзя — человек стал бы переснимать
  // то, что и так в порядке.
  photoDeniedTitle: ['Нужен доступ', 'İcazə lazımdır',
                     'Access needed', 'İzin gerekli'],
  photoDeniedBody: ['Разрешите доступ к камере и фото в настройках — иначе снять чашку или ладонь не получится. Сами снимки мы не храним.',
                    'Ayarlarda kameraya və fotolara icazə verin — əks halda fincanı və ya əli çəkmək olmayacaq. Şəkilləri özümüzdə saxlamırıq.',
                    'Allow camera and photo access in Settings — otherwise you cannot capture the cup or palm. We do not store the photos.',
                    'Ayarlarda kamera ve fotoğraf erişimine izin verin — aksi halde fincanı veya eli çekemezsiniz. Fotoğrafları saklamıyoruz.'],
  openSettings:    ['Открыть настройки', 'Ayarları aç',
                    'Open Settings', 'Ayarları aç'],
  birthTitle:     ['Когда вы родились?', 'Nə vaxt doğulmusunuz?',
                   'When were you born?', 'Ne zaman doğdunuz?'],
  birthSub:       ['По дате определим знак и сможем построить натальную карту.',
                   'Tarixə görə bürcü müəyyən edəcək və natal xəritə qura biləcəyik.',
                   'From the date we find your sign and can build your natal chart.',
                   'Tarihe göre burcunuzu belirler ve natal haritanızı kurabiliriz.'],
  birthSign:      ['Ваш знак — %s', 'Bürcünüz — %s',
                   'Your sign is %s', 'Burcunuz — %s'],
  birthDay:       ['День', 'Gün', 'Day', 'Gün'],
  birthMonth:     ['Месяц', 'Ay', 'Month', 'Ay'],
  birthYear:      ['Год', 'İl', 'Year', 'Yıl'],

  greetingMorning:  ['Доброе утро,', 'Sabahınız xeyir,',
                   'Good morning,', 'Günaydın,'],
  greetingDay:      ['Добрый день,', 'Günortanız xeyir,',
                   'Good afternoon,', 'İyi günler,'],
  greetingEvening:  ['Добрый вечер,', 'Xoş axşam,',
                   'Good evening,', 'İyi akşamlar,'],
  greetingNight:    ['Доброй ночи,', 'Gecəniz xeyrə,',
                   'Good night,', 'İyi geceler,'],
  guest:            ['Гость', 'Qonaq',
                   'Guest', 'Misafir'],

  cardOfDay:      ['Карта дня', 'Günün kartı',
                   'Card of the day', 'Günün kartı'],
  tapReveal:      ['нажмите, чтобы открыть', 'açmaq üçün toxunun',
                   'tap to reveal', 'açmak için dokunun'],
  dayHint:        ['Каждый день — новая карта и новый совет вселенной.',
                   'Hər gün — yeni kart və kainatdan yeni məsləhət.',
                   'Every day a new card and a new word from the universe.', 'Her gün yeni bir kart ve evrenden yeni bir söz.'],
  openDayCard:    ['Открыть карту дня', 'Günün kartını aç',
                   'Open the card of the day', 'Günün kartını aç'],
  makeSpread:     ['Сделать расклад', 'Açılış et',
                   'Draw a spread', 'Açılım yap'],
  quickSpreads:   ['Быстрые расклады', 'Sürətli açılışlar',
                   'Quick spreads', 'Hızlı açılımlar'],
  all:            ['Все', 'Hamısı',
                   'All', 'Tümü'],
  premiumTitle:   ['Звёзды SIRR', 'SIRR Ulduzları',
                   'SIRR Stars', 'SIRR Yıldızları'],
  premiumSub:     ['Открывают большие расклады', 'Böyük açılışları açır',
                   'They unlock the larger spreads', 'Büyük açılımları açar'],

  pickTitle:      ['Выберите карту', 'Kart seçin',
                   'Choose a card', 'Bir kart seçin'],
  pickWish:       ['Задумайте вопрос, на который можно ответить «да» или «нет».',
                   'Cavabı «bəli» və ya «xeyr» ola bilən bir sual tutun.',
                   'Think of a question that can be answered yes or no.', 'Cevabı evet ya da hayır olabilecek bir soru düşünün.'],
  pickHint:       ['Держите его в мыслях — и выберите карту, которая тянет к себе.',
                   'Onu ağlınızda saxlayın və sizi özünə çəkən kartı seçin.',
                   'Hold it in mind and choose the card that draws you.', 'Onu aklınızda tutun ve sizi çeken kartı seçin.'],
  pickChosen:     ['Карта выбрана', 'Kart seçildi',
                   'Card chosen', 'Kart seçildi'],
  answerLabel:    ['Ответ карты', 'Kartın cavabı',
                   'The card answers', 'Kartın cevabı'],
  pathsTitle:     ['Между чем выбираете?', 'Nə ilə nə arasında seçim edirsiniz?',
                   'What are you choosing between?', 'Neyle ne arasında seçim yapıyorsunuz?'],
  pathsHint:      ['Назовите два решения — карты разложатся на два пути и покажут, к чему ведёт каждый.',
                   'İki qərarı yazın — kartlar iki yola bölünüb hər birinin nəyə apardığını göstərəcək.',
                   'Name two decisions — the cards will split into two paths and show where each one leads.', 'İki kararı yazın — kartlar iki yola ayrılıp her birinin nereye götürdüğünü gösterecek.'],
  pathsFirst:     ['Первый путь', 'Birinci yol',
                   'First path', 'Birinci yol'],
  pathsSecond:    ['Второй путь', 'İkinci yol',
                   'Second path', 'İkinci yol'],
  pathsFirstHint: ['например: остаться', 'məsələn: qalmaq',
                   'for example: stay', 'örneğin: kalmak'],
  pathsSecondHint:['например: уйти', 'məsələn: getmək',
                   'for example: leave', 'örneğin: gitmek'],
  pathsOr:        ['или', 'və ya',
                   'or', 'ya da'],
  pathsGo:        ['Разложить карты', 'Kartları aç',
                   'Lay out the cards', 'Kartları aç'],
  spreadsTitle:   ['Расклады', 'Açılışlar',
                   'Spreads', 'Açılımlar'],
  spreadsIntro:   ['Выберите расклад — каждый открывает свою грань вашего вопроса.',
                   'Bir açılış seçin — hər biri sualınızın bir tərəfini açır.',
                   'Choose a spread — each one opens its own side of your question.', 'Bir açılım seçin — her biri sorunuzun başka bir yanını açar.'],
  catalogTitle:   ['Гадания', 'Fallar',
                   'Readings', 'Fallar'],
  catalogIntro:   ['Карты, кофейная гуща, ладонь, сны и те, кто говорит от своего лица.',
                   'Kartlar, qəhvə falı, ovuc, yuxular və öz dilindən danışanlar.',
                   'Cards, coffee grounds, a palm, dreams — and those who speak for themselves.',
                   'Kartlar, kahve telvesi, avuç, rüyalar ve kendi dilinden konuşanlar.'],
  tryNew:         ['Попробуйте новое', 'Yenisini yoxlayın',
                   'Try something new', 'Yeni bir şey deneyin'],
  newBadge:       ['Новое', 'Yeni',
                   'New', 'Yeni'],
  focus:          ['Тема', 'Mövzu',
                   'Focus', 'Konu'],
  focusHint:      ['О чём смотреть — можно не выбирать',
                   'Nəyə baxaq — seçməyə də bilərsiniz',
                   'What to look at — you may leave it be',
                   'Neye bakalım — seçmeyebilirsiniz'],

  free:           ['Бесплатно', 'Pulsuz',
                   'Free', 'Ücretsiz'],
  soon:           ['Скоро', 'Tezliklə',
                   'Soon', 'Yakında'],
  cards:          ['карт', 'kart',
                   'cards', 'kart'],

  readingTitle:   ['Расклад', 'Açılış',
                   'Spread', 'Açılım'],
  tapToReveal:    ['нажимайте карты, чтобы раскрыть', 'kartları açmaq üçün toxunun',
                   'tap the cards to reveal them', 'kartları açmak için dokunun'],
  revealAll:      ['Раскрыть все карты', 'Bütün kartları aç',
                   'Reveal all cards', 'Tüm kartları aç'],
  interpretation: ['Толкование', 'Şərh',
                   'Reading', 'Yorum'],
  reading:        ['Карты говорят…', 'Kartlar danışır…',
                   'The cards are speaking…', 'Kartlar konuşuyor…'],
  share:          ['Поделиться раскладом', 'Açılışı paylaş',
                   'Share this spread', 'Açılımı paylaş'],
  saved:          ['Сохранено в историю', 'Tarixçəyə yadda saxlanıldı',
                   'Saved to history', 'Geçmişe kaydedildi'],
  reversed:       ['перевёрнутая', 'tərs',
                   'reversed', 'ters'],
  favorite:       ['В избранное', 'Seçilmişlərə',
                   'Add to favourites', 'Favorilere ekle'],
  openCard:       ['Открыть карту', 'Kartı aç',
                   'Open the card', 'Kartı aç'],
  reversedCard:   ['перевёрнутая', 'tərs',
                   'reversed', 'ters'],
  closeCard:      ['Закрыть', 'Bağla',
                   'Close', 'Kapat'],
  retry:          ['Повторить', 'Yenidən cəhd et',
                   'Try again', 'Tekrar dene'],
  aiFailed:       ['Оракул молчит. Попробуйте ещё раз — карты никуда не денутся.',
                   'Oracle susur. Yenidən cəhd edin — kartlar getməyəcək.',
                   'The oracle is silent. Try again — the cards are not going anywhere.', 'Kâhin sessiz. Tekrar deneyin — kartlar bir yere gitmiyor.'],

  limitTitle:     ['Лимит на сегодня исчерпан', 'Bugünkü limit bitdi',
                   'Today\'s limit is used up', 'Bugünkü hakkınız doldu'],
  limitBody:      ['Бесплатных раскладов в день: %n. Вернитесь завтра — или откройте платный расклад за звёзды.',
                   'Gündə pulsuz açılış: %n. Sabah qayıdın — və ya ulduzlarla ödənişli açılış açın.',
                   'Free spreads per day: %n. Come back tomorrow — or open a paid spread with stars.', 'Günlük ücretsiz açılım: %n. Yarın gelin — ya da yıldızlarla ücretli bir açılım açın.'],
  limitLeft:      ['Осталось раскладов сегодня: %n', 'Bu gün qalan açılış: %n',
                   'Spreads left today: %n', 'Bugün kalan açılım: %n'],
  // Приглашение в подписи к карточке расклада. Оно дописывается сервером —
  // имя бота знает только он, — но строка живёт здесь, потому что языков
  // четыре: раньше в `share.ts` стояло `lang === 'az' ? … : …`, и англичанин
  // с турком получали русскую подпись.
  shareInvite:    ['Сделать свой расклад:', 'Öz açılışını al:',
                   'Get your own reading:', 'Kendi açılımını al:'],

  // ── Вопрос по готовому раскладу ──────────────────────────────────
  // Дополнение к раскладу: платят один раз, дальше вопросы уже оплачены.
  // Поэтому цену называем один раз, до первого вопроса, а дальше показываем
  // только остаток — иначе выглядит, будто каждый вопрос стоит денег.
  askTitle:       ['Спросить по раскладу', 'Açılış barədə soruş',
                   'Ask about this reading', 'Bu açılış hakkında sor'],
  askIntro:       ['%t вопроса о том, что осталось непонятным — %n ✦',
                   'Aydın olmayan barədə %t sual — %n ✦',
                   '%t questions about what stayed unclear — %n ✦',
                   'Anlaşılmayan hakkında %t soru — %n ✦'],
  askHint:        ['Ваш вопрос', 'Sualınız', 'Your question', 'Sorunuz'],
  askSend:        ['Спросить', 'Soruş', 'Ask', 'Sor'],
  askLeft:        ['Осталось вопросов: %n', 'Qalan sual: %n',
                   'Questions left: %n', 'Kalan soru: %n'],
  askOver:        ['Вопросы по этому раскладу кончились.', 'Bu açılış üzrə suallar bitdi.',
                   'No more questions for this reading.', 'Bu açılış için sorular bitti.'],
  askFailed:      ['Оракул молчит. Вопрос не потрачен — попробуйте ещё раз.',
                   'Oracle susur. Sual xərclənmədi — yenidən cəhd edin.',
                   'The oracle is silent. The question was not spent — try again.',
                   'Kâhin susuyor. Soru harcanmadı — tekrar deneyin.'],
  // ── Окно повышенного начисления ──────────────────────────────────
  // Цена не меняется — меняется, сколько начислим. Поэтому «×2 звёзд», а не
  // «−50%»: обещать скидку, которой в цене нет, значит обманывать.
  bonusWindowTitle: ['Сейчас звёзд вдвое больше', 'İndi ulduzlar iki dəfə çoxdur',
                     'Twice the stars right now', 'Şu anda yıldızlar iki katı'],
  bonusWindowLeft:  ['Осталось %t', 'Qalıb %t', '%t left', '%t kaldı'],
  bonusWindowNote:  ['Любой пакет даёт вдвое больше звёзд за ту же цену.',
                     'İstənilən paket eyni qiymətə iki dəfə çox ulduz verir.',
                     'Any pack gives twice the stars for the same price.',
                     'Her paket aynı fiyata iki kat yıldız verir.'],

  // ── Отложенная выдача расклада ───────────────────────────────────
  // Текст готов, но показывается не сразу. Формулируем честно: «карты
  // раскладываются», а не «идёт расчёт» — расчёта никакого нет.
  waitTitle:      ['Карты раскладываются', 'Kartlar açılır',
                   'The cards are being laid out', 'Kartlar açılıyor'],
  waitReady:      ['Толкование будет готово через %m мин.',
                   'Şərh %m dəqiqəyə hazır olacaq.',
                   'The reading will be ready in %m min.',
                   'Yorum %m dakika içinde hazır olacak.'],
  waitNow:        ['Открыть сейчас', 'İndi aç', 'Open now', 'Şimdi aç'],
  waitBySub:      ['Открывается сразу по подписке.', 'Abunəliklə dərhal açılır.',
                   'Opens right away with a subscription.', 'Abonelikle hemen açılır.'],
  // Ролик не показался или награда не дошла. Отвечать на это одной вибрацией
  // нельзя: человек нажал и сидит перед не изменившимся экраном, а вибрация
  // ничего не объясняет — тем более тому, у кого она выключена. Сказано и то,
  // что расклад не потерян: он придёт по сроку сам.
  adFailed:       ['Ролик не получился. Расклад откроется сам, когда будет готов.',
                   'Reklam alınmadı. Şərh hazır olanda özü açılacaq.',
                   'The ad did not work out. Your reading will open by itself when it is ready.',
                   'Reklam gösterilemedi. Yorumunuz hazır olduğunda kendiliğinden açılacak.'],
  // Когда реклама доступна, выходов из ожидания два, и честно назвать оба.
  waitByAd:       ['Посмотрите рекламу — или откройте сразу по подписке.',
                   'Reklama baxın — və ya abunəliklə dərhal açın.',
                   'Watch an ad — or open it right away with a subscription.',
                   'Bir reklam izleyin — ya da abonelikle hemen açın.'],

  // ── Звёзды за рекламу ────────────────────────────────────────────
  // Формулируем как заработок, а не как скидку: реклама — медленная дорога к
  // той же валюте, и путать её с ценой нельзя. Поэтому нигде не сказано
  // «бесплатно» — сказано, сколько звёзд даёт ролик.
  earnTitle:      ['Заработать звёзды', 'Ulduz qazan', 'Earn stars', 'Yıldız kazan'],
  earnDesc:       ['Короткий ролик — %n ✦', 'Qısa reklam — %n ✦',
                   'A short ad — %n ✦', 'Kısa bir reklam — %n ✦'],
  earnGo:         ['Смотреть', 'Bax', 'Watch', 'İzle'],
  earnLeft:       ['Сегодня осталось %n', 'Bu gün %n qalıb', '%n left today', 'Bugün %n kaldı'],
  // Потолок на сутки: он есть, и человек должен понимать, почему кнопки нет,
  // — иначе это выглядит поломкой.
  earnOut:        ['На сегодня всё — заходите завтра', 'Bu günlük bitdi — sabah gəlin',
                   'That is all for today — come back tomorrow',
                   'Bugünlük bu kadar — yarın tekrar gelin'],
  // Роликов может просто не оказаться в наличии. Это не ошибка приложения, и
  // говорить о ней надо спокойно.
  earnNone:       ['Ролик не показался — попробуйте позже',
                   'Reklam göstərilmədi — bir azdan yoxlayın',
                   'No ad was available — try again later',
                   'Reklam gösterilemedi — daha sonra deneyin'],
  earnDone:       ['Начислено %n ✦', '%n ✦ hesablandı', '%n ✦ added', '%n ✦ eklendi'],
  // Награда за ролик показывается ОКНОМ, а не строкой сбоку. Строку человек
  // не замечал: он только что смотрел видео во весь экран, вернулся — и на
  // прежнем месте мелкая надпись. Ради звёзд ролик и смотрят, значит и сказать
  // о них надо так, чтобы не пропустить.
  rewardTitle:    ['Звёзды ваши', 'Ulduzlar sizindir', 'The stars are yours', 'Yıldızlar sizin'],
  rewardBody:     ['Спасибо за просмотр — %n ✦ уже на счету.',
                   'Baxdığınız üçün təşəkkür — %n ✦ artıq hesabınızdadır.',
                   'Thanks for watching — %n ✦ are already in your balance.',
                   'İzlediğiniz için teşekkürler — %n ✦ şimdiden hesabınızda.'],
  rewardOk:       ['Хорошо', 'Yaxşı', 'Got it', 'Tamam'],

  // ── Подписка ─────────────────────────────────────────────────────
  // Продаёт САМ ПРОДУКТ: любые расклады и гадания, до N в день. Прежний текст
  // обещал «%n раскладов в день и %s ✦ каждый день» — и это было честное
  // описание неудачной подписки: поднятый лимит на самое дешёвое плюс звёзды
  // рассрочкой. Человек читал это как «безлимит» и упирался в цену первого же
  // настоящего расклада.
  subTitle:       ['Подписка', 'Abunəlik', 'Subscription', 'Abonelik'],
  subDesc:        ['%n любых раскладов и гаданий каждый день',
                   'Hər gün istənilən %n açılış və falçılıq',
                   'Any %n readings or fortunes every day',
                   'Her gün istediğiniz %n açılım ve fal'],
  // Второй строкой — то, что подписка снимает. Порознь эти два обещания
  // слабые, вместе складываются в «ничего не мешает».
  subPerks:       ['Без ожидания и без рекламы', 'Gözləmə yoxdur, reklam yoxdur',
                   'No waiting, no ads', 'Bekleme yok, reklam yok'],
  // Якорь: сравнение с ценой одного расклада. Число берётся из самих данных,
  // а не пишется в строке, — иначе оно разойдётся с прайсом при первой правке.
  subCompare:     ['Один расклад стоит до %c ✦ — подписка дешевле двух',
                   'Bir açılış %c ✦-ə qədərdir — abunəlik iki açılışdan ucuzdur',
                   'A single reading costs up to %c ✦ — the subscription is cheaper than two',
                   'Tek bir açılım %c ✦’a kadar — abonelik iki açılımdan ucuz'],
  // Сколько включённого осталось сегодня. Показываем только подписчику.
  subLeft:        ['Сегодня включено ещё %n', 'Bu gün daha %n daxildir',
                   '%n more included today', 'Bugün %n tane daha dahil'],
  // Метка на плитке каталога вместо цены.
  inSub:          ['Входит', 'Daxildir', 'Included', 'Dahil'],
  // Планы. Срок называем словом, а не числом дней: «Месяц» читается быстрее,
  // чем «30 дней», а разница в длине месяцев здесь никого не интересует.
  planTrial:      ['Три дня бесплатно', 'Üç gün pulsuz', 'Three days free', 'Üç gün ücretsiz'],
  planWeek:       ['Неделя', 'Bir həftə', 'A week', 'Bir hafta'],
  planMonth:      ['Месяц', 'Bir ay', 'A month', 'Bir ay'],
  planYear:       ['Год', 'Bir il', 'A year', 'Bir yıl'],
  // Пробный период МАГАЗИНА — не наш, и он висит на КАЖДОМ сроке: человек
  // выбирает месяц или год, а деньги списываются только через три дня. Поэтому
  // сказано «потом спишется», в отличие от нашего, где списывать нечего.
  // Число дней зашито в строку, потому что склонять «%n дня / %n дней» нам
  // нечем; когда магазин отдаст другой срок, показывается вариант без числа.
  planStoreTrial: ['Три дня бесплатно, потом спишется',
                   'Üç gün pulsuz, sonra ödəniş olacaq',
                   'Three days free, then it charges',
                   'Üç gün ücretsiz, sonra ödeme alınır'],
  planStoreTrialN:['Первые дни бесплатно, потом спишется',
                   'İlk günlər pulsuz, sonra ödəniş olacaq',
                   'First days free, then it charges',
                   'İlk günler ücretsiz, sonra ödeme alınır'],
  // У пробного периода цены нет — и слово «бесплатно» тут единственное честное.
  planFree:       ['Бесплатно', 'Pulsuz', 'Free', 'Ücretsiz'],
  planTrialNote:  ['Один раз, без оплаты и без отмены',
                   'Bir dəfə, ödənişsiz və ləğvsiz',
                   'Once, no payment and nothing to cancel',
                   'Bir kez, ödeme yok, iptal edilecek bir şey yok'],
  // Разовые планы Telegram сам не продлевает — обещать «автоматически» нельзя.
  planOnce:       ['Разово, без автопродления', 'Birdəfəlik, avtomatik yenilənmir',
                   'One-off, no auto-renewal', 'Tek seferlik, otomatik yenileme yok'],
  planRenews:     ['Продлевается автоматически', 'Avtomatik yenilənir',
                   'Renews automatically', 'Otomatik yenilenir'],

  // ── Льготная цена первых суток ───────────────────────────────────
  // Обещаем ровно то, что даём: дешевле стоит ПЕРВЫЙ срок, дальше цена обычная.
  // «−50% навсегда» было бы неправдой и вскрылось бы вторым списанием.
  introStrip:     ['Подписка дешевле · осталось', 'Abunəlik ucuzdur · qalıb',
                   'Subscription is cheaper · left', 'Abonelik daha ucuz · kaldı'],
  introTitle:     ['Первые сутки — дешевле', 'İlk gün — daha ucuz',
                   'Cheaper for the first day', 'İlk gün daha ucuz'],
  // Размер скидки в тексте НЕ называем, и это выстрадано: у Telegram цена наша
  // (299 → 149, то есть вдвое), а в магазине приложений её задаёт предложение
  // базового плана, и там она своя. Прежняя формулировка обещала «вдвое» на
  // всех платформах разом — на Android это было бы враньём в ту же секунду,
  // как предложение завели с другой скидкой. Настоящую цену человек видит
  // рядом, её показывает та система, которая и спишет деньги.
  introBody:      ['Оформите подписку сейчас — первый срок обойдётся дешевле обычного. Дальше цена обычная.',
                   'İndi abunə olun — ilk müddət adi qiymətdən ucuz olacaq. Sonra qiymət adi olur.',
                   'Subscribe now and the first term costs less than usual. After that the usual price.',
                   'Şimdi abone olun — ilk dönem normalden ucuz. Sonrasında normal fiyat.'],
  // Про пробный период в окне надо сказать отдельной строкой, и формулировка
  // здесь важнее обычного. Это НЕ «оформите подписку, спишем через три дня»:
  // у Telegram Stars нет ни пробных периодов, ни отложенного списания, и
  // обещать такое значило бы обещать чужую механику. Три дня даются просто
  // так — ни счёта, ни карты, ни отмены, — поэтому и сказано «ничего не
  // спишется».
  introTrial:     ['А три дня можно взять бесплатно: ничего не спишется и отменять нечего.',
                   'Üç günü isə pulsuz götürmək olar: heç nə silinmir, ləğv etmək də lazım deyil.',
                   'Or take three days free: nothing is charged and nothing to cancel.',
                   'Ya da üç günü ücretsiz alın: hiçbir ödeme alınmaz, iptal edilecek bir şey yok.'],
  introLeft:      ['осталось', 'qalıb', 'left', 'kaldı'],
  introGo:        ['Посмотреть', 'Bax', 'See the offer', 'Teklife bak'],
  introLater:     ['Позже', 'Sonra', 'Later', 'Sonra'],
  subPrice:       ['%n ✦ в месяц', 'Ayda %n ✦', '%n ✦ a month', 'Ayda %n ✦'],
  subGo:          ['Оформить', 'Rəsmiləşdir', 'Subscribe', 'Abone ol'],
  subActive:      ['Подписка активна до %d', 'Abunəlik %d tarixinədək aktivdir',
                   'Subscription active until %d', 'Abonelik %d tarihine kadar aktif'],
  subTelegramOnly:['Пока подписка оформляется только в Telegram.',
                   'Hələlik abunəlik yalnız Telegram-da rəsmiləşdirilir.',
                   'For now the subscription is available only in Telegram.',
                   'Şimdilik abonelik yalnızca Telegram üzerinden alınabiliyor.'],
  starsTitle:     ['Не хватает звёзд', 'Ulduz çatmır',
                   'Not enough stars', 'Yıldız yetmiyor'],
  starsBody:      ['Этот расклад стоит %n ✦, у вас %b ✦. Пополните баланс — и карты лягут.',
                   'Bu açılış %n ✦ dəyərindədir, sizdə %b ✦ var. Balansı artırın — kartlar açılacaq.',
                   'This spread costs %n ✦, you have %b ✦. Top up and the cards will fall.', 'Bu açılım %n ✦ değerinde, sizde %b ✦ var. Bakiyeyi artırın, kartlar açılsın.'],
  // Отдельная строка для гаданий без карт: у кофейной гущи и джинна карты
  // не лягут, и обещать это на экране оплаты — врать.
  starsBodyFortune: ['Это гадание стоит %n ✦, у вас %b ✦. Пополните баланс — и вам ответят.',
                   'Bu fal %n ✦ dəyərindədir, sizdə %b ✦ var. Balansı artırın — sizə cavab verəcəklər.',
                   'This reading costs %n ✦, you have %b ✦. Top up and you will get your answer.',
                   'Bu fal %n ✦ değerinde, sizde %b ✦ var. Bakiyeyi artırın — cevabınızı alın.'],
  notEnough:      ['Не хватает ✦', '✦ çatmır',
                   'Not enough ✦', '✦ yetmiyor'],
  // Метка на плитке гадания, пока даровое не потрачено. Короткая намеренно:
  // это подпись на плитке, а не предложение, и длинная строка на
  // азербайджанском и турецком плитку распирает.
  firstFree:      ['Первое бесплатно', 'İlki pulsuz',
                   'First one free', 'İlki ücretsiz'],
  // Анониму то же самое, но честно: даровое гадание даётся вошедшим, и
  // обещать его тому, кто не назвался, нельзя — он нажмёт и получит отказ.
  firstFreeLogin: ['Войдите — первое гадание бесплатно',
                   'Daxil olun — ilk fal pulsuz',
                   'Sign in — your first reading is free',
                   'Giriş yapın — ilk fal ücretsiz'],
  balance:        ['Ваш баланс: %n ✦', 'Balansınız: %n ✦',
                   'Your balance: %n ✦', 'Bakiyeniz: %n ✦'],

  profileTitle:   ['Профиль', 'Profil',
                   'Profile', 'Profil'],
  history:        ['История', 'Tarixçə',
                   'History', 'Geçmiş'],
  favorites:      ['Избранное', 'Seçilmişlər',
                   'Favourites', 'Favoriler'],
  stars:          ['звёзд', 'ulduz',
                   'stars', 'yıldız'],
  newSpread:      ['Новый расклад', 'Yeni açılış',
                   'New spread', 'Yeni açılım'],
  emptyHistory:   ['Здесь появятся ваши расклады.', 'Açılışlarınız burada görünəcək.',
                   'Your spreads will appear here.', 'Açılımlarınız burada görünecek.'],
  emptyFavs:      ['Отмечайте карты звёздочкой — они окажутся здесь.',
                   'Kartları ulduzla işarələyin — burada olacaqlar.',
                   'Star the cards you like — they will show up here.', 'Beğendiğiniz kartları yıldızlayın — burada toplanacaklar.'],
  levelNovice:    ['Новичок', 'Yeni başlayan',
                   'Beginner', 'Yeni başlayan'],
  levelAdept:     ['Практик', 'Təcrübəli',
                   'Practitioner', 'Deneyimli'],
  levelSeer:      ['Провидец', 'Kahin',
                   'Seer', 'Kâhin'],

  astroTitle:     ['Астрология', 'Astrologiya',
                   'Astrology', 'Astroloji'],
  compat:         ['Совместимость', 'Uyğunluq',
                   'Compatibility', 'Uyum'],
  natal:          ['Натальная карта', 'Natal xəritə',
                   'Natal chart', 'Doğum haritası'],

  yourBirth:      ['Ваша дата рождения', 'Doğum tarixiniz',
                   'Your date of birth', 'Doğum tarihiniz'],
  partnerBirth:   ['Дата рождения партнёра', 'Partnyorun doğum tarixi',
                   'Partner\'s date of birth', 'Partnerin doğum tarihi'],
  calculate:      ['Рассчитать', 'Hesabla',
                   'Calculate', 'Hesapla'],
  recalculate:    ['Рассчитать заново', 'Yenidən hesabla',
                   'Calculate again', 'Yeniden hesapla'],
  fillBoth:       ['Укажите обе даты рождения', 'Hər iki doğum tarixini göstərin',
                   'Enter both dates of birth', 'Her iki doğum tarihini girin'],
  harmony:        ['гармония', 'harmoniya',
                   'harmony', 'uyum'],
  you:            ['Вы', 'Siz',
                   'You', 'Siz'],
  partner:        ['Партнёр', 'Partnyor',
                   'Partner', 'Partner'],

  birthDate:      ['Дата рождения', 'Doğum tarixi',
                   'Date of birth', 'Doğum tarihi'],
  birthTime:      ['Время рождения', 'Doğum vaxtı',
                   'Time of birth', 'Doğum saati'],
  birthPlace:     ['Место рождения', 'Doğum yeri',
                   'Place of birth', 'Doğum yeri'],
  placeSearch:    ['Начните вводить город', 'Şəhəri yazmağa başlayın',
                   'Start typing a city', 'Şehir yazmaya başlayın'],
  placeNotFound:  ['Город не найден', 'Şəhər tapılmadı',
                   'City not found', 'Şehir bulunamadı'],
  placeOffline:   ['Поиск недоступен — показаны только основные города.',
                   'Axtarış əlçatmazdır — yalnız əsas şəhərlər göstərilir.',
                   'Search is unavailable — only major cities are shown.', 'Arama kullanılamıyor — yalnızca büyük şehirler gösteriliyor.'],
  timeUnknown:    ['Не знаю время', 'Vaxtı bilmirəm',
                   'I don\'t know the time', 'Saati bilmiyorum'],
  buildChart:     ['Построить карту', 'Xəritəni qur',
                   'Build the chart', 'Haritayı kur'],
  rebuildChart:   ['Построить заново', 'Yenidən qur',
                   'Build again', 'Yeniden kur'],
  fillBirth:      ['Укажите дату и место рождения', 'Doğum tarixini və yerini göstərin',
                   'Enter the date and place of birth', 'Doğum tarihini ve yerini girin'],
  placements:     ['Положения светил', 'Səma cisimlərinin mövqeyi',
                   'Positions of the luminaries', 'Gök cisimlerinin konumu'],
  ascendant:      ['Асцендент', 'Asendent',
                   'Ascendant', 'Yükselen'],
  midheaven:      ['Середина неба', 'Göy ortası',
                   'Midheaven', 'Gök ortası'],
  houseShort:     ['дом', 'ev',
                   'house', 'ev'],
  retro:          ['ретроградный', 'retroqrad',
                   'retrograde', 'retro'],

  noTimeNote:     ['Без времени рождения не считаются Асцендент и дома, а положение Луны может отличаться на треть знака. Карта построена на полдень.',
                   'Doğum vaxtı olmadan Asendent və evlər hesablanmır, Ayın mövqeyi bürcün üçdə biri qədər fərqlənə bilər. Xəritə günorta üçün qurulub.',
                   'Without a birth time the Ascendant and houses are not calculated, and the Moon may be off by a third of a sign. The chart is built for noon.', 'Doğum saati olmadan Yükselen ve evler hesaplanmaz, Ay\'ın konumu burcun üçte biri kadar kayabilir. Harita öğlen için kurulmuştur.'],
  cuspNote:       ['Отмеченные знаком ° положения — у самой границы знака; там точности расчёта может не хватать.',
                   '° işarəli mövqelər bürc sərhədindədir; orada hesablama dəqiqliyi çatmaya bilər.',
                   'Positions marked ° sit right on the edge of a sign; there the calculation may not be precise enough.', '° işaretli konumlar burcun tam sınırındadır; orada hesap yeterince kesin olmayabilir.'],
  outOfRangeNote: ['Дата вне диапазона 1800–2050: положения планет посчитаны приближённо.',
                   'Tarix 1800–2050 aralığından kənardır: planetlərin mövqeyi təxminidir.',
                   'The date is outside 1800–2050: planetary positions are approximate.', 'Tarih 1800–2050 aralığının dışında: gezegen konumları yaklaşıktır.'],
  astroNote:      ['Расчёт положений светил — астрономический. Толкование — традиция астрологии, а не предсказание.',
                   'Səma cisimlərinin hesablanması astronomikdir. Şərh isə astrologiya ənənəsidir, proqnoz deyil.',
                   'The positions of the luminaries are calculated astronomically. The reading is the tradition of astrology, not a prediction.', 'Gök cisimlerinin hesabı astronomiktir. Yorum ise astroloji geleneğidir, kehanet değil.'],
  readingAstro:   ['Звёзды говорят…', 'Ulduzlar danışır…',
                   'The stars are speaking…', 'Yıldızlar konuşuyor…'],
  astroFailed:    ['Оракул молчит. Расчёт на месте — попробуйте получить толкование ещё раз.',
                   'Oracle susur. Hesablama yerindədir — şərhi yenidən almağa cəhd edin.',
                   'The oracle is silent. The calculation is ready — try asking for the reading again.', 'Kâhin sessiz. Hesap yerinde — yorumu yeniden istemeyi deneyin.'],

  // ── Гадания без карт ────────────────────────────────────────────────
  howToShoot:     ['Как снимать', 'Necə çəkmək',
                   'How to shoot', 'Nasıl çekilir'],
  shoot:          ['Снять', 'Çək',
                   'Take a photo', 'Fotoğraf çek'],
  fromGallery:    ['Из галереи', 'Qalereyadan',
                   'From gallery', 'Galeriden'],
  previewHint:    ['Так и читаем? Если узор плохо видно — переснимите.',
                   'Belə oxuyaq? Naxış yaxşı görünmürsə, yenidən çəkin.',
                   'Shall we read this one? If the pattern is hard to see, take it again.',
                   'Bunu mu okuyalım? Desen iyi görünmüyorsa yeniden çekin.'],
  retake:         ['Переснять', 'Yenidən çək',
                   'Retake', 'Tekrar çek'],
  readPhoto:      ['Прочитать', 'Oxu',
                   'Read it', 'Oku'],
  fortuneCost:    ['%n ✦ за чтение', 'Oxu üçün %n ✦',
                   '%n ✦ per reading', 'Okuma başına %n ✦'],
  waitSub:        ['Обычно 5–20 секунд', 'Adətən 5–20 saniyə',
                   'Usually 5–20 seconds', 'Genellikle 5–20 saniye'],
  waitCoffee:     ['Читаю узор гущи…', 'Naxışı oxuyuram…',
                   'Reading the grounds…', 'Telveyi okuyorum…'],
  waitPalm:       ['Читаю линии ладони…', 'Ovcun xətlərini oxuyuram…',
                   'Reading the lines of your palm…', 'Avucunuzun çizgilerini okuyorum…'],
  waitDream:      ['Погружаюсь в ваш сон…', 'Yuxunuza dalıram…',
                   'Sinking into your dream…', 'Rüyanıza dalıyorum…'],
  waitDjinn:      ['Джинн пробуждается…', 'Cin oyanır…',
                   'The Djinn is waking…', 'Cin uyanıyor…'],
  waitNene:       ['Нэнэ заваривает чай…', 'Nənə çay dəmləyir…',
                   'Grandmother is brewing tea…', 'Nine çay demliyor…'],
  photoErrTitle:  ['Снимок не разобрать', 'Şəkli oxumaq olmur',
                   'The photo cannot be read', 'Fotoğraf okunamıyor'],
  photoErrBody:   ['Узора на снимке не видно. Переснимите — вот что обычно мешает:',
                   'Şəkildə naxış görünmür. Yenidən çəkin — adətən nə mane olur:',
                   'The pattern is not visible. Take it again — this is what usually gets in the way:',
                   'Desen görünmüyor. Tekrar çekin — genellikle şunlar engel olur:'],
  fortuneFailed:  ['Оракул молчит. Звёзды вернулись на счёт — попробуйте ещё раз.',
                   'Oracle susur. Ulduzlar hesaba qayıtdı — yenidən cəhd edin.',
                   'The oracle is silent. Your stars are back on the balance — try again.',
                   'Kâhin sessiz. Yıldızlarınız bakiyeye döndü — tekrar deneyin.'],
  dreamIntro:     ['Расскажите сон своими словами — чем подробнее, тем точнее толкование.',
                   'Yuxunu öz sözlərinizlə danışın — nə qədər ətraflı, bir o qədər dəqiq.',
                   'Tell the dream in your own words — the more detail, the truer the reading.',
                   'Rüyayı kendi sözlerinizle anlatın — ne kadar ayrıntılı, o kadar isabetli.'],
  dreamPlaceholder: ['Мне снилось, что…', 'Yuxumda gördüm ki…',
                   'I dreamed that…', 'Rüyamda gördüm ki…'],
  dreamJournal:   ['Дневник снов', 'Yuxu gündəliyi',
                   'Dream journal', 'Rüya günlüğü'],
  dreamJournalIntro: ['Сны возвращаются. Записанный сон легче узнать во второй раз.',
                   'Yuxular qayıdır. Yazılmış yuxunu ikinci dəfə tanımaq asandır.',
                   'Dreams come back. A written-down dream is easier to recognise the second time.',
                   'Rüyalar geri döner. Yazılmış bir rüyayı ikinci kez tanımak kolaydır.'],
  dreamEmptyTitle:['Дневник пока пуст', 'Gündəlik hələ boşdur',
                   'The journal is still empty', 'Günlük şimdilik boş'],
  dreamEmptyBody: ['Запишите сон, пока он не растаял, — и он останется здесь.',
                   'Yuxu əriməmiş yazın — o burada qalacaq.',
                   'Write the dream down before it melts away — it will stay here.',
                   'Rüya uçup gitmeden yazın — burada kalacak.'],
  dreamRecord:    ['Записать сон', 'Yuxu yaz',
                   'Record a dream', 'Rüya yaz'],
  dreamInterpret: ['Растолковать сон', 'Yuxunu yoz',
                   'Interpret the dream', 'Rüyayı yorumla'],
  dreamSaved:     ['Сохранено в дневник', 'Gündəliyə yazıldı',
                   'Saved to the journal', 'Günlüğe kaydedildi'],
  wishLabel:      ['Ваше желание', 'Arzunuz',
                   'Your wish', 'Dileğiniz'],
  wishPlaceholder:['Например: покой в сердце', 'Məsələn: ürək rahatlığı',
                   'For example: peace in my heart', 'Örneğin: kalbimde huzur'],
  wishMake:       ['Загадать желание', 'Arzu tut',
                   'Make a wish', 'Dilek tut'],
  askPlaceholder: ['Спросите нэнэ…', 'Nənədən soruşun…',
                   'Ask grandmother…', 'Nineye sorun…'],
  turnsLeft:      ['Осталось: %n', 'Qalıb: %n',
                   '%n left', 'Kalan: %n'],
  talkResume:     ['Продолжить разговор', 'Söhbəti davam et',
                   'Continue the talk', 'Sohbete devam et'],
  talkUnfinished: ['Разговор уже начат и оплачен', 'Söhbət başlayıb və ödənilib',
                   'The talk is already begun and paid for', 'Sohbet başladı ve ödendi'],
  replyFailed:    ['Ответа не дождались. Ход не потрачен — попробуйте ещё раз.',
                   'Cavab gəlmədi. Növbə yanmadı — yenidən cəhd edin.',
                   'No answer came. The turn was not spent — try again.',
                   'Cevap gelmedi. Hakkınız yanmadı — tekrar deneyin.'],
  neneHear:       ['Услышать нэнэ', 'Nənəni dinlə',
                   'Hear grandmother', 'Nineyi dinle'],
  dialogue:       ['Разговор', 'Söhbət',
                   'The conversation', 'Sohbet'],
  fortuneAgain:   ['Погадать заново', 'Yenidən bax',
                   'Read again', 'Yeniden bak'],

  premiumIntro:   ['Звёзды открывают гадания, которых нет в бесплатном наборе',
                   'Ulduzlar pulsuz dəstdə olmayan falları açır',
                   'Stars unlock the readings that are not in the free set',
                   'Yıldızlar ücretsiz sette olmayan falları açar'],
  whatStarsBuy:   ['Что открывают звёзды', 'Ulduzlar nəyi açır',
                   'What stars unlock', 'Yıldızlar neyi açar'],
  freeTierNote:   ['Карта дня, расклад на три карты, совместимость и натальная карта остаются бесплатными — %n раза в день.',
                   'Günün kartı, üç kartlıq açılış, uyğunluq və natal xəritə pulsuz qalır — gündə %n dəfə.',
                   'The card of the day, the three-card spread, compatibility and the natal chart stay free — %n times a day.', 'Günün kartı, üç kartlık açılım, uyum ve doğum haritası ücretsiz kalır — günde %n kez.'],
  cardsCount:     ['%n карт', '%n kart',
                   '%n cards', '%n kart'],
  buyStars:       ['Пополнить Stars', 'Stars artır',
                   'Top up Stars', 'Stars yükle'],
  popular:        ['Выбор', 'Seçim',
                   'Pick', 'Seçim'],
  bonus:          ['бонус', 'bonus',
                   'bonus', 'bonus'],
  starsNote:      ['Разовая оплата через Telegram Stars. Звёзды не сгорают и списываются только за платный расклад.',
                   'Telegram Stars ilə birdəfəlik ödəniş. Ulduzlar yanmır və yalnız ödənişli açılışa xərclənir.',
                   'A one-time payment through Telegram Stars. Stars do not expire and are spent only on paid spreads.', 'Telegram Stars ile tek seferlik ödeme. Yıldızlar yanmaz ve yalnızca ücretli açılımda harcanır.'],
  starsNotePwa:   ['Разовая оплата картой или криптой. Цена указана в долларах — банк спишет эквивалент в вашей валюте. Звёзды не сгорают.',
                   'Kart və ya kripto ilə birdəfəlik ödəniş. Qiymət dollarladır — bankınız valyutanızda ekvivalenti silər. Ulduzlar yanmır.',
                   'A one-time payment by card or crypto. The price is in dollars — your bank charges the equivalent in your currency. Stars do not expire.', 'Kart ya da kripto ile tek seferlik ödeme. Fiyat dolar üzerinden — bankanız kendi paranızda karşılığını çeker. Yıldızlar yanmaz.'],
  pay:            ['Оплатить', 'Ödə',
                   'Pay', 'Öde'],

  next:           ['Далее', 'İrəli',
                   'Next', 'İleri'],
  start:          ['Начать путь', 'Yola başla',
                   'Begin the path', 'Yola çık'],
  /*
    Кнопка на ПОСЛЕДНЕМ шаге знакомства.

    Здесь стояло `enter` — та же строка, что на кнопке настоящего входа:
    «Войти», «Sign in», «Daxil ol». Кнопка при этом никуда не входит, а просто
    закрывает знакомство и открывает главную; аккаунт к этому моменту уже есть,
    анонимный. Но прочесть подпись можно только одним способом — «дальше меня
    заставят зарегистрироваться», — и именно так её и прочли: сперва владелец,
    а следом прочёл бы ревьюер App Store, у которого на это есть пункт 5.1.1(v)
    (нельзя требовать регистрацию, когда аккаунт для работы не нужен).
    Поймано 2026-09-05 на симуляторе.

    Поэтому подпись говорит, куда ведёт: к картам.
  */
  /*
    Полоса «поставьте приложение» на сайте, только для Android.

    Пишем про то, что человек получит, а не про то, что нам удобнее: на
    телефоне приложение из Play заметно лучше сайта — напоминания приходят
    надёжнее, и всё работает без браузера. Про звёзды тут не обещаем ничего:
    награда за установку живёт у PWA, и мешать два разных обещания в одной
    полосе — верный способ не выполнить одно из них.
  */
  getApp:         ['Приложение для Android', 'Android üçün tətbiq',
                   'Android app', 'Android uygulaması'],
  getAppSub:      ['Напоминания приходят надёжнее, и работает без браузера.',
                   'Xatırlatmalar daha etibarlı gəlir, brauzer olmadan işləyir.',
                   'Reminders arrive more reliably, and it works without a browser.',
                   'Hatırlatmalar daha güvenilir gelir, tarayıcısız çalışır.'],
  getAppBtn:      ['Установить', 'Quraşdır',
                   'Install', 'Yükle'],

  obEnter:        ['К картам', 'Kartlara keç',
                   'To the cards', 'Kartlara geç'],

  /*
    Окно «обновитесь», из которого нет выхода, кроме магазина.

    Показывается, только когда сервер назвал минимальную сборку и наша ниже.
    Текст объясняет ПРИЧИНУ, а не командует: человек не виноват, что у него
    выключено автообновление, и «обновите приложение» без объяснения читается
    как каприз. Слово «работать» здесь не запугивание — окно и вправду
    показывается лишь тогда, когда старую сборку обслуживать нечем.
  */
  updateTitle:    ['Нужно обновление', 'Yeniləmə lazımdır',
                   'Update required', 'Güncelleme gerekli'],
  updateBody:     ['Эта версия приложения больше не работает с сервером. Обновитесь — история, звёзды и подписка останутся на месте.',
                   'Tətbiqin bu versiyası artıq server ilə işləmir. Yeniləyin — tarixçə, ulduzlar və abunəlik yerində qalacaq.',
                   'This version of the app no longer works with the server. Update it — your history, stars and subscription stay as they are.',
                   'Uygulamanın bu sürümü artık sunucuyla çalışmıyor. Güncelleyin — geçmişiniz, yıldızlarınız ve aboneliğiniz yerinde kalır.'],
  updateBtn:      ['Обновить', 'Yenilə',
                   'Update', 'Güncelle'],
  newReading:     ['Новый расклад', 'Yeni açılış',
                   'New spread', 'Yeni açılım'],
  // Правило изменилось 2026-09-04: раз в день не «расклад», а «расклад на одну
  // тему». Прежний текст («сегодня выберите другой») звал уйти к другому
  // раскладу, хотя человеку нужен тот же — просто про другое.
  // Расклад под выбранной темой сегодня уже сделан: кнопка обязана сказать
  // правду. Она звала «Новый расклад», а открывала прежний — владелец нажал и
  // не понял, списалось у него что-то или нет.
  showMade:       ['Показать сделанный расклад', 'Edilmiş açılışı göstər',
                   'Show the reading you made', 'Yaptığınız açılımı göster'],
  onceADay:       ['Этот расклад делается раз в день на одну тему. Хотите ещё — выберите другую тему, и карты лягут заново.',
                   'Bu açılış gündə bir mövzu üzrə bir dəfə edilir. Yenə istəsəniz — başqa mövzu seçin, kartlar yenidən düzüləcək.',
                   'This spread is drawn once a day per topic. Want another — pick a different topic and the cards will fall anew.',
                   'Bu açılım günde bir konu için bir kez yapılır. Yine isterseniz — başka bir konu seçin, kartlar yeniden dizilir.'],
  enter:          ['Войти', 'Daxil ol',
                   'Sign in', 'Giriş yap'],

  loginSub:       ['Войдите, чтобы карты помнили ваш путь на всех устройствах.',
                   'Kartların yolunuzu bütün cihazlarda xatırlaması üçün daxil olun.',
                   'Sign in so the cards remember your path on every device.', 'Kartların yolunuzu her cihazda hatırlaması için giriş yapın.'],
  loginGoogle:    ['Войти через Google', 'Google ilə daxil ol',
                   'Sign in with Google', 'Google ile giriş yap'],
  loginTelegram:  ['Продолжить в Telegram', 'Telegram-də davam et',
                   'Continue in Telegram', 'Telegram\'da devam et'],
  loginError:     ['Не удалось войти. Попробуйте ещё раз.', 'Daxil olmaq alınmadı. Yenidən cəhd edin.',
                   'Could not sign in. Please try again.', 'Giriş yapılamadı. Lütfen tekrar deneyin.'],
  logout:         ['Выйти', 'Çıxış',
                   'Sign out', 'Çıkış yap'],
  // Предложение войти — не стена, а объяснение выгоды. Появляется в профиле у
  // того, кто ещё не назвался, и на пейволле вместо «пополните баланс».
  anonTitle:      ['История живёт только здесь',
                   'Tarixçə yalnız bu cihazda saxlanılır',
                   'Your history lives only on this device',
                   'Geçmişiniz yalnızca bu cihazda'],
  anonWhy:        ['Войдите, и расклады, звёзды и подписка переживут переустановку и откроются на другом устройстве.',
                   'Daxil olun — açılışlar, ulduzlar və abunə yenidən quraşdırmadan sonra da qalacaq və başqa cihazda açılacaq.',
                   'Sign in and your readings, stars and subscription will survive a reinstall and open on another device.',
                   'Giriş yapın; açılımlarınız, yıldızlarınız ve aboneliğiniz yeniden kurulumdan sonra da kalır ve başka cihazda açılır.'],
  anonToBuy:      ['Войдите, чтобы покупать',
                   'Almaq üçün daxil olun',
                   'Sign in to buy',
                   'Satın almak için giriş yapın'],
  anonToBuyWhy:   ['Звёзды привязаны к аккаунту — без него они пропадут вместе с приложением.',
                   'Ulduzlar hesaba bağlıdır — hesabsız onlar tətbiqlə birlikdə itəcək.',
                   'Stars belong to an account — without one they vanish with the app.',
                   'Yıldızlar hesaba bağlıdır; hesap olmadan uygulamayla birlikte kaybolur.'],
  // Вторая строка предложения войти. Первая — `firstFreeLogin`, подарок;
  // здесь то, что человек получает вдобавок. Порядок важен: до этой правки
  // анониму показывали только предупреждение «звёзды пропадут», то есть
  // причину бояться вместо причины войти.
  anonFreeWhy:    ['А звёзды и история останутся при вас: без аккаунта они живут только на этом телефоне.',
                   'Ulduzlar və tarixçə də sizinlə qalacaq: hesabsız onlar yalnız bu telefonda yaşayır.',
                   'Your stars and history stay with you too — without an account they live only on this phone.',
                   'Yıldızlarınız ve geçmişiniz de sizinle kalır: hesap olmadan yalnızca bu telefonda yaşarlar.'],

  // Приглашения анониму не показываем кодом, а зовём войти: награда живёт
  // только на настоящем аккаунте, и выданный код был бы обещанием, которого мы
  // не выполним. Про друга сказано прямо — он тоже должен войти, иначе человек
  // приглашает, ждёт звёзд и не понимает, почему их нет.
  // Предложение установить на экран — с обещанием звёзд.
  //
  // Обещание должно быть в самом заголовке, а не мелким шрифтом ниже: без него
  // это просто «поставьте наше приложение», а с ним — обмен, понятный за
  // секунду. Число подставляется с сервера, зашивать его в текст нельзя: оно
  // настраивается переменной окружения и разъедется при первой же правке.
  installTitle:   ['Поставьте SIRR на экран — %n ✦ в подарок',
                   'SIRR-i ekrana əlavə edin — hədiyyə %n ✦',
                   'Add SIRR to your home screen — %n ✦ as a gift',
                   'SIRR’i ana ekrana ekleyin — hediye %n ✦'],
  installBody:    ['Открывается как обычное приложение, без браузера. И напоминание о карте дня приходит только так.',
                   'Adi tətbiq kimi açılır, brauzersiz. Günün kartı barədə xatırlatma da yalnız belə gəlir.',
                   'It opens like a normal app, without the browser. And the daily card reminder only works this way.',
                   'Normal bir uygulama gibi açılır, tarayıcısız. Günün kartı hatırlatması da yalnızca böyle gelir.'],
  installGo:      ['Установить', 'Quraşdır', 'Install', 'Yükle'],
  installLater:   ['Не сейчас', 'İndi yox', 'Not now', 'Şimdi değil'],
  // Награда уже начислена: человек открыл приложение с домашнего экрана.
  installDone:    ['Спасибо — приложение на экране',
                   'Təşəkkür — tətbiq ekrandadır',
                   'Thank you — the app is on your screen',
                   'Teşekkürler — uygulama ana ekranınızda'],
  installDoneBody: ['%n ✦ уже на счету. Теперь SIRR открывается в одно касание.',
                   '%n ✦ artıq hesabınızdadır. İndi SIRR bir toxunuşla açılır.',
                   '%n ✦ are already in your balance. SIRR now opens with one tap.',
                   '%n ✦ şimdiden hesabınızda. SIRR artık tek dokunuşla açılıyor.'],

  // Подписи справочных страниц (`scripts/make-content.ts`): значения карт и
  // описания раскладов, собранные в статический HTML для поиска. Страницы
  // публичные и читаются людьми, а не только роботами, — поэтому строки живут
  // здесь и уходят на вычитку носителю вместе с остальными.
  seoCardsTitle:  ['Карты Таро', 'Taro kartları', 'Tarot cards', 'Tarot kartları'],
  seoSpreadsTitle: ['Расклады Таро', 'Taro açılışları', 'Tarot spreads', 'Tarot açılımları'],
  seoUpright:     ['Прямое положение', 'Düz vəziyyət', 'Upright', 'Düz konum'],
  seoReversed:    ['Перевёрнутое положение', 'Tərs vəziyyət', 'Reversed', 'Ters konum'],
  seoMajor:       ['Старшие арканы', 'Böyük arkanlar', 'Major arcana', 'Büyük arkana'],
  seoMinor:       ['Младшие арканы', 'Kiçik arkanlar', 'Minor arcana', 'Küçük arkana'],
  seoPositions:   ['Позиции в раскладе', 'Açılışdakı mövqelər', 'Positions in the spread',
                   'Açılımdaki konumlar'],
  seoOpenApp:     ['Погадать в приложении', 'Tətbiqdə fala baxın', 'Try it in the app',
                   'Uygulamada deneyin'],
  seoCardDesc:    ['%s: значение карты Таро в прямом и перевёрнутом положении.',
                   '%s: Taro kartının düz və tərs vəziyyətdə mənası.',
                   '%s: the meaning of the tarot card upright and reversed.',
                   '%s: tarot kartının düz ve ters konumdaki anlamı.'],
  seoSpreadDesc:  ['%s — расклад Таро на %n карт: что означает каждая позиция.',
                   '%s — %n kartlıq Taro açılışı: hər mövqenin mənası.',
                   '%s — a tarot spread of %n cards: what each position means.',
                   '%s — %n kartlık Tarot açılımı: her konumun anlamı.'],

  // Заголовок и описание вкладки. Не украшение: их читает поисковик, их же
  // показывает мессенджер, когда человек делится ссылкой — а делятся у нас
  // ссылкой с реферальным кодом. В статическом `index.html` они по-русски
  // (страница одна на четыре языка), здесь — переписываются под выбранный.
  docTitle:       ['SIRR — Таро, гадания и астрология',
                   'SIRR — Taro, fallar və astrologiya',
                   'SIRR — Tarot, fortune telling and astrology',
                   'SIRR — Tarot, fal ve astroloji'],
  docDesc:        ['Карта дня, расклады, гадание по кофейной гуще и ладони, натальная карта.',
                   'Günün kartı, açılışlar, qəhvə falı və ovuc falı, natal xəritə.',
                   'Card of the day, spreads, coffee and palm reading, natal chart.',
                   'Günün kartı, açılımlar, kahve ve el falı, natal harita.'],

  anonToInvite:   ['Войдите, чтобы приглашать',
                   'Dəvət etmək üçün daxil olun',
                   'Sign in to invite friends',
                   'Arkadaş davet etmek için giriş yapın'],
  anonToInviteWhy: ['Звёзды за друга приходят на аккаунт. Друг тоже входит — иначе награду некому и не за кого начислить.',
                   'Dostunuza görə ulduzlar hesaba gəlir. Dost da daxil olmalıdır — əks halda mükafatı nə kimə, nə nəyə görə yazmaq olar.',
                   'Stars for a friend land in an account. Your friend signs in too — otherwise there is no one to reward and nothing to count.',
                   'Arkadaşınız için yıldızlar hesaba gelir. Arkadaşınız da giriş yapar — aksi hâlde ödülü ne kime ne de neye göre yazmak mümkün olur.'],

  // Удаление учётной записи. Google Play требует, чтобы обещанное на странице
  // удаления работало, а не делалось руками по письму.
  deleteTitle:    ['Удалить учётную запись', 'Hesabı sil',
                   'Delete account', 'Hesabı sil'],
  // В подписи перечислено то, что исчезнет: человек должен понимать цену
  // до нажатия, а не после.
  deleteSub:      ['Расклады, гадания, звёзды и профиль исчезнут навсегда',
                   'Açılışlar, falllar, ulduzlar və profil həmişəlik silinəcək',
                   'Readings, fortunes, stars and profile will be gone forever',
                   'Açılımlar, fallar, yıldızlar ve profil kalıcı olarak silinecek'],
  deleteAsk:      ['Удалить всё безвозвратно?', 'Hər şey birdəfəlik silinsin?',
                   'Delete everything permanently?', 'Her şey kalıcı olarak silinsin mi?'],
  deleteYes:      ['Да, удалить', 'Bəli, sil',
                   'Yes, delete', 'Evet, sil'],
  deleteFailed:   ['Не удалось удалить. Попробуйте позже.', 'Silmək alınmadı. Sonra cəhd edin.',
                   'Could not delete. Please try again later.', 'Silinemedi. Lütfen daha sonra deneyin.'],
  deleteNo:       ['Отмена', 'İmtina',
                   'Cancel', 'Vazgeç'],
  notifyTitle:    ['Уведомления', 'Bildirişlər',
                   'Notifications', 'Bildirimler'],
  notifySub:      ['Напоминание про карту дня и акции', 'Günün kartı və aksiyalar barədə xatırlatma',
                   'A reminder about the card of the day and offers', 'Günün kartı ve kampanyalar için hatırlatma'],
  // Токена не дали: устройство без сервисов Google или эмулятор. Отдельно от
  // «разрешите в настройках» — там человеку есть что сделать, здесь нечего,
  // и посылать его в настройки значит гонять впустую.
  notifyNoDevice: ['Это устройство не умеет получать уведомления',
                   'Bu cihaz bildiriş ala bilmir',
                   'This device cannot receive notifications',
                   'Bu cihaz bildirim alamıyor'],
  notifyBlocked:  ['Разрешите уведомления в настройках устройства', 'Cihaz parametrlərində bildirişlərə icazə verin',
                   'Allow notifications in your device settings', 'Cihaz ayarlarından bildirimlere izin verin'],

  inviteTitle:    ['Пригласи друзей', 'Dostları dəvət et',
                   'Invite friends', 'Arkadaşlarını davet et'],
  inviteDesc:     ['За каждого друга — %perFriend ✦ (до %cap друзей).',
                   'Hər dost üçün %perFriend ✦ (%cap dosta qədər).',
                   'Each friend brings you %perFriend ✦ (up to %cap friends).',
                   'Her arkadaş için %perFriend ✦ (%cap arkadaşa kadar).'],
  inviteStats:    ['Друзей: %n · заработано: %b ✦', 'Dostlar: %n · qazanılıb: %b ✦',
                   'Friends: %n · earned: %b ✦', 'Arkadaş: %n · kazanılan: %b ✦'],
  inviteShare:    ['Поделиться ссылкой', 'Linki paylaş',
                   'Share the link', 'Bağlantıyı paylaş'],
  inviteCopied:   ['Ссылка скопирована ✓', 'Link kopyalandı ✓',
                   'Link copied ✓', 'Bağlantı kopyalandı ✓'],
  inviteMsg:      ['Таро и астрология с AI — попробуй SIRR:', 'AI ilə Taro və astrologiya — SIRR-i yoxla:',
                   'Tarot and astrology with AI — try SIRR:', 'Yapay zekâ ile tarot ve astroloji — SIRR\'ı dene:'],
  invitesTitle:   ['Приглашения', 'Dəvətlər',
                   'Invitations', 'Davetler'],
  inviteHeadline: ['Приглашай друзей —\nполучай звёзды', 'Dostları dəvət et —\nulduz qazan',
                   'Invite friends —\nearn stars', 'Arkadaşlarını davet et —\nyıldız kazan'],
  inviteProgress: ['%n из %cap друзей', '%cap-dan %n dost',
                   '%n of %cap friends', '%cap arkadaştan %n'],
  // Заработанное рядом с полосой прогресса.
  inviteEarned:   ['%b ✦', '%b ✦', '%b ✦', '%b ✦'],
  inviteToMax:    ['Ещё %n — и максимум %max ✦', 'Daha %n — və maksimum %max ✦',
                   '%n more and you reach %max ✦', '%n tane daha — ve maksimum %max ✦'],
  inviteMaxed:    ['Максимум! %max ✦ заработано', 'Maksimum! %max ✦ qazanıldı',
                   'Maxed out! %max ✦ earned', 'Tamamlandı! %max ✦ kazanıldı'],
  inviteHow:      ['Друг открывает по твоей ссылке и делает первый расклад — ты получаешь %bonus ✦, а он — %stars ✦ в подарок.',
                   'Dost linkinlə açır və ilk açılışı edir — sən %bonus ✦, o isə hədiyyə %stars ✦ alır.',
                   'A friend opens your link and draws their first spread — you get %bonus ✦, and they get %stars ✦ as a gift.',
                   'Arkadaşın bağlantınla açar ve ilk açılımını yapar — sen %bonus ✦, o ise hediye %stars ✦ alır.'],
  inviteCopyLink: ['Скопировать ссылку', 'Linki kopyala',
                   'Copy the link', 'Bağlantıyı kopyala'],
  paywallInviteTitle: ['Не хочешь платить?', 'Ödəmək istəmirsən?',
                   'Don\'t want to pay?', 'Ödemek istemiyor musun?'],
  paywallInviteDesc:  ['Пригласи друга — получи %bonus ✦.',
                       'Dost dəvət et — %bonus ✦ qazan.',
                       'Invite a friend and get %bonus ✦.',
                       'Bir arkadaşını davet et — %bonus ✦ kazan.'],

  loginOr:        ['или', 'və ya',
                   'or', 'ya da'],
  loginEmail:     ['Почта', 'E-poçt',
                   'Email', 'E-posta'],
  loginPassword:  ['Пароль', 'Parol',
                   'Password', 'Parola'],
  loginName:      ['Имя', 'Ad',
                   'Name', 'Ad'],
  loginSignIn:    ['Войти', 'Daxil ol',
                   'Sign in', 'Giriş yap'],
  loginSignUp:    ['Создать аккаунт', 'Hesab yarat',
                   'Create an account', 'Hesap oluştur'],
  loginToRegister:['Нет аккаунта? Создать', 'Hesabınız yoxdur? Yaradın',
                   'No account? Create one', 'Hesabın yok mu? Oluştur'],
  loginToLogin:   ['Уже есть аккаунт? Войти', 'Hesabınız var? Daxil olun',
                   'Already have an account? Sign in', 'Hesabın var mı? Giriş yap'],
  loginBadEmail:  ['Проверьте адрес почты.', 'E-poçt ünvanını yoxlayın.',
                   'Check the email address.', 'E-posta adresini kontrol edin.'],
  loginBadPass:   ['Пароль от 8 символов.', 'Parol ən azı 8 simvol.',
                   'Password must be at least 8 characters.', 'Parola en az 8 karakter olmalı.'],
  loginTaken:     ['Эта почта уже зарегистрирована.', 'Bu e-poçt artıq qeydiyyatdan keçib.',
                   'This email is already registered.', 'Bu e-posta zaten kayıtlı.'],
  loginBadCreds:  ['Неверная почта или пароль.', 'E-poçt və ya parol yanlışdır.',
                   'Wrong email or password.', 'E-posta ya da parola yanlış.'],

  langTitle:      ['Язык', 'Dil', 'Language', 'Dil'],
  dailyTitle:     ['Прогноз на день', 'Günün proqnozu', 'Today’s forecast', 'Günün öngörüsü'],
  dailySub:       ['Что сегодня в небе — и что это значит для вас',
                   'Bu gün səmada nə var — və bu sizin üçün nə deməkdir',
                   'What the sky holds today — and what it means for you',
                   'Bugün gökyüzünde ne var — ve bu sizin için ne anlama geliyor'],
  dailyNeedBirth: ['Укажите дату рождения — по ней считается ваш знак.',
                   'Doğum tarixini göstərin — bürcünüz ona görə hesablanır.',
                   'Enter your date of birth — your sign is calculated from it.',
                   'Doğum tarihinizi girin — burcunuz buna göre hesaplanır.'],
  dailyGet:       ['Показать прогноз', 'Proqnozu göstər', 'Show the forecast', 'Öngörüyü göster'],
  notifyAskTitle: ['Напоминать про карту дня?', 'Günün kartını xatırladaq?',
                   'Remind you about the card of the day?', 'Günün kartını hatırlatalım mı?'],
  notifyAskBtn:   ['Включить', 'Aç', 'Turn on', 'Aç'],
  notifyLater:    ['Позже', 'Sonra', 'Later', 'Sonra'],
  // Предложение с наградой — отдельно от обычной просьбы (`notifyAskTitle`):
  // там мы просто спрашиваем, здесь меняем звёзды на согласие, и обещание
  // должно стоять в заголовке, а не мелким шрифтом ниже.
  notifyBonusTitle: ['Включите напоминания — %n ✦ в подарок',
                   'Xatırlatmaları aktivləşdirin — hədiyyə %n ✦',
                   'Turn on reminders — %n ✦ as a gift',
                   'Hatırlatmaları açın — hediye %n ✦'],
  notifyBonusBody: ['Раз в день напомним про карту дня — и назовём её. Отключить можно в профиле.',
                   'Gündə bir dəfə günün kartını xatırladacağıq və adını deyəcəyik. Profildən söndürmək olar.',
                   'Once a day we will remind you about the card of the day and name it. You can turn it off in your profile.',
                   'Günde bir kez günün kartını hatırlatacağız ve adını söyleyeceğiz. Profilden kapatabilirsiniz.'],
  notifyBonusDone: ['Напоминания включены',
                   'Xatırlatmalar aktivdir',
                   'Reminders are on',
                   'Hatırlatmalar açık'],
  notifyBonusDoneBody: ['%n ✦ уже на счету. Первое напоминание придёт вечером.',
                   '%n ✦ artıq hesabınızdadır. İlk xatırlatma axşam gələcək.',
                   '%n ✦ are already in your balance. The first reminder arrives in the evening.',
                   '%n ✦ şimdiden hesabınızda. İlk hatırlatma akşam gelecek.'],
  // То же окно, но анониму: ему звёзд не начисляют вовсе, поэтому обещать
  // награду за напоминания ему нельзя — сперва вход. Обе суммы называются
  // сразу: человек должен видеть, что вход это не условие, а первая половина
  // подарка.
  /*
    Зовём подарком, который что-то стоит.

    Раньше здесь обещали десять звёзд — на них нельзя купить НИЧЕГО, самое
    дешёвое платное стоит 30. Теперь вход открывает первое гадание, и число из
    заголовка убрано: подставлять туда ноль было бы издевательством, а
    называть звёзды, которых больше не дают, — обманом.
  */
  loginBonusTitle: ['Войдите — первое гадание в подарок',
                   'Daxil olun — ilk fal hədiyyə',
                   'Sign in — your first reading is a gift',
                   'Giriş yapın — ilk fal hediye'],
  loginBonusBody: ['История и звёзды останутся с вами — на любом устройстве.',
                   'Tarixçəniz və ulduzlarınız sizinlə qalacaq — istənilən cihazda.',
                   'Your history and stars stay with you — on any device.',
                   'Geçmişiniz ve yıldızlarınız sizinle kalır — her cihazda.'],
  // Вторая половина обещания. Показывается только когда напоминания человеку
  // действительно можно предложить: если он уже отказал системному запросу,
  // включить их нечем, и обещание было бы ложным.
  loginBonusThen: ['А за включённые напоминания дадим ещё %n ✦.',
                   'Xatırlatmaları aktivləşdirsəniz, daha %n ✦ verəcəyik.',
                   'And we will add another %n ✦ once you turn on reminders.',
                   'Hatırlatmaları açarsanız, %n ✦ daha vereceğiz.'],
  loginBonusDone: ['С возвращением',
                   'Xoş gəldiniz',
                   'Welcome',
                   'Hoş geldiniz'],
  loginBonusDoneBody: ['%n ✦ за вход уже на счету.',
                   'Daxil olduğunuz üçün %n ✦ artıq hesabınızdadır.',
                   '%n ✦ for signing in are already in your balance.',
                   'Giriş yaptığınız için %n ✦ şimdiden hesabınızda.'],
  /*
    То же самое, но когда даровое гадание ещё не потрачено.

    Отдельной строкой, а не приклеенной фразой: человека позвали войти именно
    обещанием бесплатного гадания, и подтвердить его надо в ту секунду, когда
    он вернулся, — иначе он узнает о подарке, только случайно дойдя до
    каталога. Тому, кто вошёл в старый аккаунт с потраченным гаданием,
    показывается прежняя строка: обещать ему нечего.
  */
  // Когда за вход не начисляют звёзд вовсе (так с 2026-09-10), говорить надо
  // только про гадание: «0 ✦ на счету» — это насмешка.
  loginFreeOnly:  ['Первое гадание — бесплатно. Выбирайте любое.',
                   'İlk fal pulsuzdur. İstədiyinizi seçin.',
                   'Your first reading is free. Pick any one.',
                   'İlk falınız ücretsiz. Dilediğinizi seçin.'],
  loginBonusFree: ['%n ✦ за вход уже на счету. И первое гадание — бесплатно, выбирайте любое.',
                   'Daxil olduğunuz üçün %n ✦ artıq hesabınızdadır. İlk fal isə pulsuzdur — istədiyinizi seçin.',
                   '%n ✦ for signing in are already in your balance. And your first reading is free — pick any.',
                   'Giriş yaptığınız için %n ✦ hesabınızda. İlk falınız da ücretsiz — dilediğinizi seçin.'],
  // Наше объяснение ПЕРЕД системным окном отслеживания на iOS. Системное окно
  // показывается один раз за установку, и отказ в нём необратим — поэтому
  // сначала говорим человеку, о чём его спросят, и о чём НЕ спросят.
  attTitle:       ['Разрешить учёт установки?', 'Quraşdırmanın uçotuna icazə?',
                   'Allow install tracking?', 'Kurulum sayımına izin verilsin mi?'],
  attBody:        ['Чтобы мы понимали, откуда приходят новые люди, и не показывали рекламу тем, кто уже установил приложение. Ваши вопросы и гадания к этому отношения не имеют и никуда не передаются.',
                   'Yeni insanların haradan gəldiyini bilmək və tətbiqi artıq quraşdıranlara reklam göstərməmək üçün. Suallarınızın və fallarınızın buna aidiyyəti yoxdur və heç yerə ötürülmür.',
                   'So we can see where new people come from and avoid showing ads to those who already installed the app. Your questions and readings have nothing to do with this and are not shared.',
                   'Yeni kullanıcıların nereden geldiğini anlamak ve uygulamayı zaten kuranlara reklam göstermemek için. Sorularınızın ve fallarınızın bununla ilgisi yoktur ve hiçbir yere aktarılmaz.'],
  attAllow:       ['Продолжить', 'Davam et', 'Continue', 'Devam et'],
  // На Android системного окна нет, и наша кнопка — само решение, а не переход
  // к нему. «Продолжить» там означало бы «дальше будет ещё вопрос», а его нет.
  attAllowNow:    ['Разрешить', 'İcazə ver', 'Allow', 'İzin ver'],
  attSkip:        ['Не сейчас', 'İndi yox', 'Not now', 'Şimdi değil'],
  installText:    ['Установить SIRR на телефон', 'SIRR-i telefona quraşdır',
                   'Install SIRR on your phone', 'SIRR\'ı telefonuna kur'],
  installBtn:     ['Установить', 'Quraşdır',
                   'Install', 'Kur'],
  installIos:     ['Откройте меню «Поделиться» и выберите «На экран „Домой“»',
                   '«Paylaş» menyusunu açıb «Ana ekrana əlavə et» seçin',
                   'Open the Share menu and choose “Add to Home Screen”', 'Paylaş menüsünü açıp “Ana Ekrana Ekle” seçin'],
} as const

export type TKey = keyof typeof DICT

/** Все ключи словаря — нужны для выгрузки текстов на вычитку. */
export const I18N_KEYS = Object.keys(DICT) as TKey[]

/** Порядок языков внутри каждой строки словаря. */
const ORDER: Lang[] = ['ru', 'az', 'en', 'tr']

/**
 * Названия языков — на самих себе, а не переведённые: так их узнают, не понимая
 * текущего языка приложения. Поэтому это и не строка словаря: переводить
 * «Azərbaycan» на русский было бы ровно тем, чего мы избегаем.
 *
 * В shared, потому что читателей четыре: профиль и вход в вебе, профиль и вход
 * в нативе. Копий было две — пока одинаковые, но третья и четвёртая довели бы
 * до расхождения, как это уже случилось со строками интерфейса.
 */
export const LANG_NAMES: Record<Lang, string> = {
  ru: 'Русский',
  az: 'Azərbaycan',
  en: 'English',
  tr: 'Türkçe',
}

export function makeT(lang: Lang) {
  const i = Math.max(0, ORDER.indexOf(lang))
  return (key: TKey, vars?: Record<string, string | number>): string => {
    // Недопереведённая строка не должна оставлять пустоту: откатываемся на
    // английский, затем на русский.
    const row = DICT[key] as readonly string[]
    let s: string = row[i] || row[2] || row[0]
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`%${k}`, String(v))
    return s
  }
}

export type T = ReturnType<typeof makeT>

const MONTHS: Record<Lang, string[]> = {
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
       'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  az: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun',
       'iyul', 'avqust', 'sentyabr', 'oktyabr', 'noyabr', 'dekabr'],
  en: ['January', 'February', 'March', 'April', 'May', 'June',
       'July', 'August', 'September', 'October', 'November', 'December'],
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
       'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
}

/**
 * Название месяца само по себе — для выбора месяца списком.
 *
 * Отдельно от `MONTHS`, потому что там русские месяцы в родительном падеже:
 * они собираются в «5 января». В списке нужен именительный — «Январь», а не
 * «января». В остальных трёх языках месяц не склоняется вовсе, и там это те же
 * слова; расхождение ровно одно и только в русском.
 */
const MONTHS_NOM: string[] = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

export function monthName(month: number, lang: Lang): string {
  const i = Math.min(11, Math.max(0, month - 1))
  return lang === 'ru' ? MONTHS_NOM[i] : MONTHS[lang][i]
}

export function formatDate(ts: number, lang: Lang): string {
  const d = new Date(ts)
  const month = MONTHS[lang][d.getMonth()]
  // В английском день после месяца: «5 January» звучит по-русски, а не по-английски.
  return lang === 'en' ? `${month} ${d.getDate()}` : `${d.getDate()} ${month}`
}

/**
 * Склонение слова «расклад». В азербайджанском существительное после
 * числительного не изменяется, поэтому там всегда одна форма.
 */
export function spreadsPlural(n: number, lang: Lang): string {
  if (lang === 'az') return 'açılış'
  // В турецком существительное после числительного не меняется, как и в
  // азербайджанском; в английском множественное число простое.
  if (lang === 'tr') return 'açılım'
  if (lang === 'en') return n === 1 ? 'spread' : 'spreads'
  const mod100 = n % 100
  const mod10 = n % 10
  if (mod100 >= 11 && mod100 <= 14) return 'раскладов'
  if (mod10 === 1) return 'расклад'
  if (mod10 >= 2 && mod10 <= 4) return 'расклада'
  return 'раскладов'
}

export function greetingKey(): TKey {
  const h = new Date().getHours()
  if (h < 5) return 'greetingNight'
  if (h < 12) return 'greetingMorning'
  if (h < 18) return 'greetingDay'
  return 'greetingEvening'
}
